import React from 'react';
import { Menu, MenuButton, MenuItems, MenuItem, MenuSection, MenuHeading, MenuSeparator } from '@headlessui/react';

/** A selectable action with optional grouping and supporting content. */
interface DropdownMenuItem {
  /** Display label. */
  label: string;
  /** Click handler. */
  onClick: (e: React.MouseEvent) => void;
  /** Optional icon element. */
  icon?: React.ReactNode;
  /** Text color class (e.g. 'text-danger'). */
  className?: string;
  /** When true, the item is rendered greyed out and is not selectable. */
  disabled?: boolean;
  /** Start a labeled group, continuing until another section or separator. */
  section?: string;
  /** Separate this action from the preceding group without adding a heading. */
  separatorBefore?: boolean;
  /** Noninteractive supporting content at the end of the row. */
  trailing?: React.ReactNode;
}

/** Presentation and placement options for an accessible action menu. */
interface DropdownMenuProps {
  /** Trigger element. */
  trigger: React.ReactNode;
  /** Menu items. */
  items: DropdownMenuItem[];
  /** Alignment of the dropdown. */
  align?: 'left' | 'right';
  /** Anchor position for the dropdown panel (Headless UI anchor prop). Defaults to manual absolute positioning. */
  anchor?: 'bottom start' | 'bottom end' | 'top start' | 'top end';
  /** Width class override (default: 'w-48'). */
  width?: string;
  /** Explicit z-index for the panel. Needed when the trigger lives inside a
   *  higher-stacking-context container (e.g. the mobile sidebar drawer at
   *  zIndex 51) because Headless UI portals the panel to body level. */
  panelZIndex?: number;
  /** Compact inset rows are opt-in; existing menus retain their presentation. */
  appearance?: 'default' | 'compact';
}

/** Adjacent actions sharing one optional accessible heading. */
interface DropdownMenuGroup {
  heading?: string;
  separatorBefore: boolean;
  entries: Array<{ item: DropdownMenuItem; index: number }>;
}

/** Preserve action order while splitting only at explicit group boundaries. */
function groupMenuItems(items: DropdownMenuItem[]): DropdownMenuGroup[] {
  const groups: DropdownMenuGroup[] = [];
  items.forEach((item, index) => {
    let group = groups[groups.length - 1];
    if (!group || item.section || item.separatorBefore) {
      group = { heading: item.section, separatorBefore: index > 0, entries: [] };
      groups.push(group);
    }
    group.entries.push({ item, index });
  });
  return groups;
}

/**
 * Accessible dropdown menu built on Headless UI Menu.
 * Provides keyboard navigation, focus management, and auto-close on select.
 */
export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  trigger,
  items,
  align = 'right',
  anchor,
  width = 'w-48',
  panelZIndex,
  appearance = 'default',
}) => {
  const compact = appearance === 'compact';

  /** Headless UI continues to own selection, disabled state and focus. */
  const renderItem = (item: DropdownMenuItem, index: number) => (
    <MenuItem key={index} disabled={item.disabled}>
      {({ active }) => (
        <button
          onClick={item.onClick}
          disabled={item.disabled}
          className={`flex min-h-11 w-full touch-manipulation items-center text-left text-sm transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-volt motion-reduce:transition-none ${
            compact ? 'md:min-h-9 gap-2 rounded-md px-2 py-2' : 'gap-3 px-4 py-3'
          } ${active ? 'bg-surface-raised-hover' : ''} ${
            item.disabled
              ? 'text-subtle-foreground cursor-not-allowed'
              : `${item.className || 'text-muted-foreground'}${compact ? ' cursor-pointer' : ''}`
          } ${!compact && index > 0 && !item.section && !item.separatorBefore ? 'border-t border-border' : ''}`}
        >
          {compact && item.icon ? <span className="shrink-0">{item.icon}</span> : item.icon}
          {compact || item.trailing
            ? <span className="min-w-0 flex-1">{item.label}</span>
            : item.label}
          {item.trailing && <>{' '}<span className="ml-auto shrink-0">{item.trailing}</span></>}
        </button>
      )}
    </MenuItem>
  );

  const grouped = items.some(item => item.section || item.separatorBefore);

  return (
    <Menu as="div" className="relative">
      <MenuButton as={React.Fragment}>{trigger}</MenuButton>

      <MenuItems transition
        {...(anchor ? { anchor: { to: anchor, gap: 4 } } : {})}
        style={panelZIndex !== undefined ? { zIndex: panelZIndex } : undefined}
        className={`transition duration-200 data-closed:opacity-0 data-closed:-translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none ${anchor ? '' : `absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1`} ${width} bg-surface-raised border border-border rounded-lg z-20 overflow-hidden focus:outline-none${compact ? ' p-1' : ''}`}
      >
        {grouped ? groupMenuItems(items).map((group, index) => (
          <React.Fragment key={index}>
            {group.separatorBefore && <MenuSeparator className="my-1 h-px bg-border/60" />}
            {group.heading ? (
              <MenuSection>
                <MenuHeading className={`${compact ? 'px-2' : 'px-4'} py-2 text-sm font-normal text-muted-foreground`}>
                  {group.heading}
                </MenuHeading>
                {group.entries.map(entry => renderItem(entry.item, entry.index))}
              </MenuSection>
            ) : group.entries.map(entry => renderItem(entry.item, entry.index))}
          </React.Fragment>
        )) : items.map(renderItem)}
      </MenuItems>
    </Menu>
  );
};
